import { useState } from "react";
import { useGstRegister, useTdsRegister, type Period } from "@/api/books";
import { PeriodPicker, thisFy } from "@/components/books/PeriodPicker";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { formatDate, formatInr, formatMoney } from "@/lib/format";

const TH = "px-4 py-2 font-medium";
const NUM = "whitespace-nowrap px-4 py-2 text-right tabular-nums";

function TdsView({ period }: { period: Period }) {
  const register = useTdsRegister(period);
  if (!register.data) return <RequestError error={register.error} />;
  if (register.data.rows.length === 0) {
    return <EmptyState title="No TDS in this period" description="Bills with TDS appear here by section and month." />;
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {register.data.by_section.map((row) => (
          <Badge key={row.section} variant="secondary">
            {row.section}: {formatInr(row.tds_inr)} on {formatInr(row.amount_inr)}
          </Badge>
        ))}
        {register.data.by_month.map((row) => (
          <Badge key={row.month} variant="warning">
            {row.month}: {formatInr(row.tds_inr)} due {formatDate(row.deposit_due)}
          </Badge>
        ))}
      </div>
      <div className="table-frame">
        <table className="data-table">
          <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className={TH}>Date</th>
              <th className={TH}>Payee</th>
              <th className={TH}>Bill</th>
              <th className={TH}>Section</th>
              <th className={`${TH} text-right`}>Bill INR</th>
              <th className={`${TH} text-right`}>TDS INR</th>
              <th className={TH}>Deposit by</th>
            </tr>
          </thead>
          <tbody>
            {register.data.rows.map((row) => (
              <tr key={row.expense_id} className="border-b last:border-0">
                <td className="whitespace-nowrap px-4 py-2">{formatDate(row.spent_on)}</td>
                <td className="px-4 py-2">{row.payee}</td>
                <td className="px-4 py-2">{row.description}</td>
                <td className="px-4 py-2">{row.section}</td>
                <td className={NUM}>{formatInr(row.amount_inr)}</td>
                <td className={NUM}>{formatInr(row.tds_inr)}</td>
                <td className="whitespace-nowrap px-4 py-2">{formatDate(row.deposit_due)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GstView({ period }: { period: Period }) {
  const register = useGstRegister(period);
  if (!register.data) return <RequestError error={register.error} />;
  const { output, input } = register.data;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        {register.data.gstin
          ? `GSTIN ${register.data.gstin}: input credit of ${formatInr(register.data.total_itc_inr)} is eligible.`
          : "No GSTIN yet: the GST inside bills stays in the expense, and exports are shown as zero-rated."}
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="table-frame">
          <table className="data-table">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className={TH}>Output (invoices)</th>
                <th className={TH}>Date</th>
                <th className={`${TH} text-right`}>Value</th>
                <th className={`${TH} text-right`}>Tax</th>
                <th className={`${TH} text-right`}>Posted INR</th>
              </tr>
            </thead>
            <tbody>
              {output.map((row) => (
                <tr key={row.invoice_id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    {row.invoice_no}
                    <span className="block text-xs text-muted-foreground">{row.client}</span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">{formatDate(row.invoice_date)}</td>
                  <td className={NUM}>{formatMoney(row.subtotal, row.currency)}</td>
                  <td className={NUM}>
                    {row.zero_rated ? <Badge variant="muted">zero-rated</Badge> : formatMoney(row.tax, row.currency)}
                  </td>
                  <td className={NUM}>{row.posted_inr ? formatInr(row.posted_inr) : "—"}</td>
                </tr>
              ))}
              {output.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-muted-foreground" colSpan={5}>
                    No invoices in this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="table-frame">
          <table className="data-table">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className={TH}>Input (bills)</th>
                <th className={TH}>Date</th>
                <th className={`${TH} text-right`}>Bill INR</th>
                <th className={`${TH} text-right`}>GST INR</th>
                <th className={TH}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {input.map((row) => (
                <tr key={row.expense_id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    {row.description}
                    <span className="block text-xs text-muted-foreground">{row.payee}</span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">{formatDate(row.spent_on)}</td>
                  <td className={NUM}>{formatInr(row.amount_inr)}</td>
                  <td className={NUM}>{formatInr(row.gst_inr)}</td>
                  <td className="px-4 py-2">{row.itc_eligible ? "eligible" : "not eligible"}</td>
                </tr>
              ))}
              <tr className="bg-muted/30 font-medium">
                <td className="px-4 py-2" colSpan={3}>
                  Total GST in bills
                </td>
                <td className={NUM}>{formatInr(register.data.total_input_gst_inr)}</td>
                <td className="px-4 py-2">{formatInr(register.data.total_itc_inr)} creditable</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function TaxPanel() {
  const [period, setPeriod] = useState<Period>(thisFy());
  return (
    <div className="flex flex-col gap-6">
      <PeriodPicker value={period} onChange={setPeriod} />
      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold">TDS register</h2>
        <TdsView period={period} />
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold">GST register</h2>
        <GstView period={period} />
      </section>
    </div>
  );
}
